import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { debounce } from 'lodash';

const ContributorTag = React.memo(({ name, active, onClick }) => (
    <StyledContributorTag $active={active} onClick={onClick}>
        {name}
    </StyledContributorTag>
));

const ContributorSection = ({ contributors, selectedContributors, onContributorsChange }) => {
    const [activeContributors, setActiveContributors] = useState(new Set());

    useEffect(() => {
        setActiveContributors(new Set(selectedContributors.map(c => c.email)));
    }, [selectedContributors]);

    const debouncedOnContributorsChange = useMemo(
        () => debounce(onContributorsChange, 100),
        [onContributorsChange]
    );

    const toggleContributor = useCallback((email) => {
        setActiveContributors(prev => {
            const newSet = new Set(prev);
            if (newSet.has(email)) {
                newSet.delete(email);
            } else {
                newSet.add(email);
            }
            return newSet;
        });
    }, []);

    useEffect(() => {
        const activeContributorObjects = contributors.filter(c => activeContributors.has(c.email));
        debouncedOnContributorsChange(activeContributorObjects);

        return () => {
            debouncedOnContributorsChange.cancel();
        };
    }, [activeContributors, contributors, debouncedOnContributorsChange]);

    const memoizedContributors = useMemo(() => contributors, [contributors]);

    return (
        <ContentWrapper>
            {memoizedContributors.map((contributor) => (
                <ContributorTag
                    key={contributor.email}
                    name={contributor.name}
                    active={activeContributors.has(contributor.email)}
                    onClick={() => toggleContributor(contributor.email)}
                />
            ))}
        </ContentWrapper>
    );
};

const ContentWrapper = styled.div`
    align-self: start;
    display: flex;
    margin-top: 8px;
    align-items: start;
    gap: 4px;
    font-size: 14px;
    font-weight: 500;
    flex-wrap: wrap;
`;

const StyledContributorTag = styled.span`
  font-family: Inter, sans-serif;
  font-weight: 500;
  border-radius: 10px;
  border: 1px solid rgba(234, 235, 234, 1);
  background-color: ${props => props.$active ? "#534af5" : "#fff"};
  color: ${props => props.$active ? "#fff" : "#000"};
  justify-content: center;
  padding: 12px 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  user-select: none;
  &:hover {
    background-color: ${props => props.$active ? "#7161ef" : "#f0f0f0"};
  }
`;

export default React.memo(ContributorSection);